import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import avatecLogo from '../../assets/logos/avatec-white.png';
import './Slide10RND.css';

const activities = [
  'Software development for marine systems',
  'Hardware design and engineering',
  'Tailor-made solutions',
  'Integration and testing for shipboard environments',
];

const SEGMENTS = [
  { label: 'Senior Hardware Eng.',        value: 12, color: '#0D9488' },
  { label: 'Senior Software Eng.',        value: 15, color: '#0369A1' },
  { label: 'Junior Hardware Eng.',        value: 5,  color: '#0891B2' },
  { label: 'Junior Software Eng.',        value: 4,  color: '#2563EB' },
  { label: 'Quality Eng.',                value: 2,  color: '#DC2626' },
  { label: '3D And Graphics',             value: 3,  color: '#7C3AED' },
  { label: 'Academic Prof. Supervisor',   value: 2,  color: '#D97706' },
  { label: 'Production Tech.',            value: 4,  color: '#059669' },
];

const TOTAL = SEGMENTS.reduce((s, d) => s + d.value, 0);

// ── SVG helpers ──────────────────────────────────────────────────────────────

function polar(cx: number, cy: number, r: number, deg: number) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function donutArc(
  cx: number, cy: number,
  outerR: number, innerR: number,
  start: number, end: number,
): string {
  const o1 = polar(cx, cy, outerR, start);
  const o2 = polar(cx, cy, outerR, end);
  const i1 = polar(cx, cy, innerR, end);
  const i2 = polar(cx, cy, innerR, start);
  const lg = end - start > 180 ? 1 : 0;
  return [
    `M ${o1.x} ${o1.y}`,
    `A ${outerR} ${outerR} 0 ${lg} 1 ${o2.x} ${o2.y}`,
    `L ${i1.x} ${i1.y}`,
    `A ${innerR} ${innerR} 0 ${lg} 0 ${i2.x} ${i2.y}`,
    'Z',
  ].join(' ');
}

// ── Donut chart ──────────────────────────────────────────────────────────────

function DonutChart({ visible }: { visible: boolean }) {
  const cx = 160, cy = 160;
  const outerR = 148, innerR = 82;
  const GAP = 3;

  let angle = -90;
  const segs = SEGMENTS.map((seg) => {
    const full  = (seg.value / TOTAL) * 360;
    const sweep = Math.max(full - GAP, 1);
    const start = angle + GAP / 2;
    const end   = start + sweep;
    angle += full;
    return { ...seg, start, end };
  });

  return (
    <svg viewBox="0 0 320 320" width="100%" height="100%" style={{ display: 'block' }}>
      {segs.map((seg, i) => (
        <motion.path
          key={i}
          d={donutArc(cx, cy, outerR, innerR, seg.start, seg.end)}
          fill={seg.color}
          initial={{ opacity: 0, scale: 0.85 }}
          animate={visible ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.1 + i * 0.07, ease: 'easeOut' }}
          style={{ transformOrigin: `${cx}px ${cy}px` }}
        />
      ))}


    </svg>
  );
}

// ── Slide ────────────────────────────────────────────────────────────────────

function Slide10RND() {
  const prefersReduced = useReducedMotion();
  const reduceMotion   = !!prefersReduced;
  const [isVisible, setIsVisible] = useState(false);
  const ease: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94];

  useEffect(() => { setIsVisible(true); }, []);

  const fadeUp = (delay = 0) =>
    reduceMotion
      ? { initial: { opacity: 1 }, animate: { opacity: 1 } }
      : {
          initial: { opacity: 0, y: 20 },
          animate: isVisible ? { opacity: 1, y: 0 } : {},
          transition: { duration: 0.7, delay, ease },
        };

  return (
    <div className="slide slide-rnd">
      <img src={avatecLogo} alt="Avatec Marine" className="slide-logo" />

      <div className="rnd-bg">
        <div className="rnd-grid" />
        <div className="rnd-orb rnd-orb-left" />
        <div className="rnd-orb rnd-orb-right" />
        <div className="rnd-noise" />
      </div>

      <div className="rnd-content">
        {/* ── Left column ── */}
        <motion.div className="rnd-left" {...fadeUp(0.05)}>
          <h1 className="rnd-title">
            Research &amp;
            <br />
            Development Expertise
          </h1>
          <p className="rnd-lead">
            Avatec Marine continuously invests in research and development to advance marine technology solutions,
            ensuring our clients stay ahead with cutting-edge innovation.
          </p>

          <div className="rnd-section">
            <div className="rnd-section-title">CORE R&amp;D ACTIVITIES</div>
            <ul className="rnd-list">
              {activities.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          {/* 47+ stat */}

        </motion.div>

        {/* ── Right column ── */}
        <motion.div className="rnd-chart-col" {...fadeUp(0.15)}>

          {/* Animasyonlu 47+ kart */}
          <motion.div
            className={`rnd-card ${reduceMotion ? 'rnd-card--static' : ''}`}
            {...fadeUp(0.15)}
          >
            <div className="rnd-card-bg" />
            <div className="rnd-card-grid" />
            <div className="rnd-card-orbit" />
            <div className="rnd-card-glow" />
            <div className="rnd-card-content">
              <div className="rnd-card-number">47+</div>
              <div className="rnd-card-label">ENGINEERS</div>
              <p className="rnd-card-text">
                The R&amp;D team develops innovative marine technologies and customized solutions,
                driving continuous improvement and future-oriented projects.
              </p>
            </div>
          </motion.div>

          {/* Donut + Legend yan yana */}
          <div className="rnd-chart-row">
            <div className="rnd-donut-wrap">
              <DonutChart visible={isVisible} />
            </div>

            <div className="rnd-legend">
              {SEGMENTS.map((seg, i) => (
                <motion.div
                  key={seg.label}
                  className="rnd-legend-item"
                  initial={{ opacity: 0, x: 10 }}
                  animate={isVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.4 + i * 0.05, ease: 'easeOut' }}
                >
                  <span className="rnd-legend-dot" style={{ background: seg.color }} />
                  <span className="rnd-legend-text">
                    <span className="rnd-legend-name">{seg.label}</span>
                    <span className="rnd-legend-val">{seg.value}</span>
                  </span>
                </motion.div>
              ))}
            </div>
          </div>

        </motion.div>
      </div>
    </div>
  );
}

export default Slide10RND;
